import { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { AlegraSyncButton } from '../components/AlegraSyncButton';
import RefreshContactoCard from '../components/alegra/RefreshContactoCard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Progress } from '../components/ui/progress';
import { Switch } from '../components/ui/switch';
import { 
  RefreshCw, 
  CheckCircle, 
  XCircle, 
  Download, 
  Users, 
  FileText, 
  Receipt, 
  CreditCard,
  Building2,
  Eye,
  EyeOff,
  AlertCircle,
  Loader2,
  StopCircle,
  Clock,
  Calendar,
  Settings,
  Play,
  Send,
  Search
} from 'lucide-react';

export const AlegraIntegration = () => {
  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [importStatus, setImportStatus] = useState(null);
  const pollIntervalRef = useRef(null);
  
  // Sync config state
  const [syncConfig, setSyncConfig] = useState({
    enabled: false,
    schedule_hour: 2,
    schedule_minute: 0,
    import_contacts: true,
    import_invoices: true,
    import_bills: true,
    import_payments: true,
    notification_emails: [],
    alegra_email: '',
    alegra_token: '',
    // Iter 235.m (Carlos): Capa 4 — auto-asociar DTE entrante a OC
    auto_asociar_dte_enabled: true,
    auto_asociar_dte_threshold: 90,
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [notificationEmail, setNotificationEmail] = useState('');
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [testingEmail, setTestingEmail] = useState(false);
  
  // Import options
  const [importOptions, setImportOptions] = useState({
    contacts: true,
    invoices: true,
    bills: true,
    payments: true
  });

  // Load saved credentials from server-side config
  useEffect(() => {
    const loadCredentials = async () => {
      try {
        const res = await api.get('/alegra/sync-config');
        if (res.data?.alegra_email) setEmail(res.data.alegra_email);
        if (res.data?.alegra_token) setToken(res.data.alegra_token);
      } catch { /* credentials not saved yet */ }
    };
    loadCredentials();
  }, []);

  // Load stats, check import status, and load sync config on mount
  useEffect(() => {
    loadStats();
    checkImportStatus();
    loadSyncConfig();
    loadSchedulerStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, []);

  const loadStats = async () => {
    setLoadingStats(true);
    try {
      const res = await api.get('/alegra/stats');
      setStats(res.data);
    } catch (err) { console.error('caught:', err); } finally {
      setLoadingStats(false);
    }
  };

  const loadSyncConfig = async () => {
    try {
      const res = await api.get('/alegra/sync-config');
      setSyncConfig(res.data);
      if (res.data.notification_emails?.length > 0) {
        setNotificationEmail(res.data.notification_emails[0]);
      }
    } catch (err) { console.error('caught:', err); }
  };

  const loadSchedulerStatus = async () => {
    try {
      const res = await api.get('/alegra/scheduler-status');
      setSchedulerStatus(res.data);
    } catch (err) { console.error('caught:', err); }
  };

  const saveSyncConfig = async () => {
    setSavingConfig(true);
    try {
      const configToSave = {
        ...syncConfig,
        notification_emails: notificationEmail ? [notificationEmail] : [],
        alegra_email: email,
        alegra_token: token || syncConfig.alegra_token
      };
      
      await api.post('/alegra/sync-config', configToSave);
      await loadSyncConfig();
      await loadSchedulerStatus();
      alert('Configuración guardada correctamente');
    } catch (err) {
      alert(err.response?.data?.detail || 'Error guardando configuración');
    } finally {
      setSavingConfig(false);
    }
  };

  const triggerSyncNow = async () => {
    try {
      await api.post('/alegra/sync-now');
      setImporting(true);
      startPolling();
    } catch (err) {
      alert(err.response?.data?.detail || 'Error iniciando sincronización');
    }
  };

  const sendTestEmail = async () => {
    if (!notificationEmail) {
      alert('Ingrese un email de notificación primero');
      return;
    }
    
    setTestingEmail(true);
    try {
      // Save config first to ensure email is set
      await api.post('/alegra/sync-config', {
        ...syncConfig,
        notification_emails: [notificationEmail],
        alegra_email: email,
        alegra_token: token || syncConfig.alegra_token
      });
      
      const res = await api.post('/alegra/test-email');
      alert(res.data.message);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error enviando email de prueba');
    } finally {
      setTestingEmail(false);
    }
  };

  const checkImportStatus = async () => {
    try {
      const res = await api.get('/alegra/import-status');
      setImportStatus(res.data);
      
      if (res.data.running) {
        setImporting(true);
        startPolling();
      }
    } catch (err) { console.error('caught:', err); }
  };

  const startPolling = () => {
    if (pollIntervalRef.current) return;
    
    pollIntervalRef.current = setInterval(async () => {
      try {
        const res = await api.get('/alegra/import-status');
        setImportStatus(res.data);
        
        if (!res.data.running) {
          stopPolling();
          setImporting(false);
          loadStats();
          loadSyncConfig(); // Reload to get last_sync info
        }
      } catch (err) { console.error('caught:', err); }
    }, 2000);
  };

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const testConnection = async () => {
    if (!email || !token) {
      alert('Ingrese email y token');
      return;
    }
    
    setTesting(true);
    setConnectionStatus(null);
    
    try {
      const res = await api.post('/alegra/test-connection', { email, token });
      setConnectionStatus({
        success: true,
        message: res.data.message,
        company: res.data.company_name
      });
      
      // Credentials are now managed server-side via sync-config
    } catch (err) {
      setConnectionStatus({
        success: false,
        message: err.response?.data?.detail || 'Error de conexión'
      });
    } finally {
      setTesting(false);
    }
  };

  const importData = async () => {
    if (!email || !token) {
      alert('Primero pruebe la conexión');
      return;
    }
    
    setImporting(true);
    setImportStatus(null);
    
    try {
      const res = await api.post('/alegra/import', {
        email,
        token,
        import_contacts: importOptions.contacts,
        import_invoices: importOptions.invoices,
        import_bills: importOptions.bills,
        import_payments: importOptions.payments
      });
      
      setImportStatus(res.data.status);
      startPolling();
    } catch (err) {
      setImporting(false);
      alert(err.response?.data?.detail || 'Error al iniciar importación');
    }
  };

  const cancelImport = async () => {
    try {
      await api.post('/alegra/cancel-import');
      stopPolling();
      setImporting(false);
      checkImportStatus();
    } catch (err) { console.error('caught:', err); }
  };

  // Carlos (08/05/2026): catch-up rápido cuando el daily falló o llevamos
  // días sin sync. Trae bills + invoices + credit_notes + payments de los
  // últimos 7 días en una sola llamada. ~5-15 seg.
  const [syncingPending, setSyncingPending] = useState(false);
  const [lastIncrementalResult, setLastIncrementalResult] = useState(null);
  const syncPendingNow = async () => {
    setSyncingPending(true);
    try {
      const res = await api.post('/alegra/sync-incremental?force_extended=true');
      setLastIncrementalResult(res.data);
      // Refresh stats after sync
      loadStats();
      checkImportStatus();
    } catch (err) {
      alert('Error en sync incremental: ' + (err?.response?.data?.detail || err.message));
    }
    setSyncingPending(false);
  };

  // Iter 232.13 (Carlos) — Resync FULL de bills/invoices pendientes (cierra gaps del incremental).
  const [resyncing, setResyncing] = useState(false);
  const [resyncResult, setResyncResult] = useState(null);
  const [resyncJob, setResyncJob] = useState(null);

  const pollJob = async (jobId) => {
    let attempts = 0;
    while (attempts < 90) {  // hasta 90 * 2s = 3 min
      try {
        const res = await api.get(`/alegra/resync/job/${jobId}`);
        setResyncJob(res.data);
        if (res.data.status === 'done' || res.data.status === 'error') {
          return res.data;
        }
      } catch {
        // Job no encontrado o error de red — sigue intentando.
      }
      await new Promise((r) => setTimeout(r, 2000));
      attempts++;
    }
    return null;
  };

  const resyncPendingBills = async () => {
    if (!window.confirm('Resincronizar TODAS las facturas de COMPRA pendientes (saldo > 0) desde Alegra?\n\nPuede tardar 30-90 seg. Útil cuando un proveedor reclame que su saldo no coincide.')) return;
    setResyncing(true);
    setResyncResult(null);
    setResyncJob({ status: 'queued' });
    try {
      const { data } = await api.post('/alegra/resync/bills-pending');
      const final = await pollJob(data.job_id);
      if (final) {
        setResyncResult({ tipo: 'Compra', ...final });
        loadStats();
      }
    } catch (err) {
      alert('Error: ' + (err?.response?.data?.detail || err.message));
    }
    setResyncing(false);
  };

  const resyncPendingInvoices = async () => {
    if (!window.confirm('Resincronizar TODAS las facturas de VENTA pendientes (saldo > 0) desde Alegra?')) return;
    setResyncing(true);
    setResyncResult(null);
    setResyncJob({ status: 'queued' });
    try {
      const { data } = await api.post('/alegra/resync/invoices-pending');
      const final = await pollJob(data.job_id);
      if (final) {
        setResyncResult({ tipo: 'Venta', ...final });
        loadStats();
      }
    } catch (err) {
      alert('Error: ' + (err?.response?.data?.detail || err.message));
    }
    setResyncing(false);
  };

  const resyncBillByNumber = async () => {
    const numero = window.prompt('Número de factura de COMPRA a rescatar puntualmente desde Alegra (ej: 268854):');
    if (!numero) return;
    setResyncing(true);
    try {
      const res = await api.post(`/alegra/resync/bill-by-number?numero=${encodeURIComponent(numero.trim())}`);
      alert(`✓ Factura ${res.data.numero} de ${res.data.proveedor} rescatada.\nFecha: ${res.data.fecha}\nSaldo: $${(res.data.saldo || 0).toLocaleString('es-CL')}\nEstado: ${res.data.estado}`);
      loadStats();
    } catch (err) {
      alert('Error: ' + (err?.response?.data?.detail || err.message));
    }
    setResyncing(false);
  };

  const fmt = (n) => {
    if (!n && n !== 0) return '-';
    return Math.round(n).toLocaleString('es-CL');
  };

  const getStepProgress = (step, status) => {
    if (!status || !status.progress) return { percent: 0, text: 'Pendiente' };

    const stepStatus = status.progress[`${step}_status`];
    const fetched = status.progress[`${step}_fetched`] || 0;
    const total = status.progress[`${step}_total`] || 0;
    const saved = status.progress[`${step}_saved`] || 0;
    const imported = status.progress[`${step}_imported`] || 0;
    const updated = status.progress[`${step}_updated`] || 0;

    // Si la importación NO está corriendo pero esta etapa quedó en "saving"
    // o "fetching" → es un quiebre (cancel del usuario o crash). Mostrar como
    // INTERRUMPIDO con el conteo parcial; no mostrar spinner.
    const isInterrupted = !status.running && (stepStatus === 'saving' || stepStatus === 'fetching');
    if (isInterrupted) {
      const partial = saved > 0 ? `${saved}/${total}` : (total > 0 ? `0/${total}` : `${fetched} obtenidos`);
      return { percent: total > 0 ? Math.round((saved / total) * 100) : 0, text: `Interrumpido — ${partial}`, interrupted: true };
    }

    if (stepStatus === 'completed') {
      return { percent: 100, text: `Completado: ${imported} nuevos, ${updated} actualizados` };
    }
    if (stepStatus === 'cancelled') {
      const partial = saved > 0 ? `${saved}/${total}` : (total > 0 ? `0/${total}` : `${fetched} obtenidos`);
      return { percent: total > 0 ? Math.round((saved / total) * 100) : 0, text: `Cancelado — ${partial}`, interrupted: true };
    }
    if (stepStatus === 'skipped') {
      return { percent: 100, text: 'Omitido (requiere plan premium)' };
    }
    if (stepStatus === 'saving') {
      const percent = total > 0 ? Math.round((saved / total) * 100) : 50;
      return { percent, text: `Guardando ${saved}/${total}...` };
    }
    if (stepStatus === 'fetching') {
      return { percent: 30, text: `Obteniendo ${fetched} registros...` };
    }
    return { percent: 0, text: 'Pendiente' };
  };

  const renderProgressSection = () => {
    if (!importStatus) return null;
    
    const steps = [
      { key: 'contacts', label: 'Contactos', icon: Users, color: 'blue' },
      { key: 'invoices', label: 'Facturas de Venta', icon: FileText, color: 'green' },
      { key: 'bills', label: 'Facturas de Compra', icon: Receipt, color: 'orange' },
      { key: 'payments', label: 'Pagos Recibidos', icon: CreditCard, color: 'purple' },
      { key: 'payment_outs', label: 'Pagos Realizados', icon: CreditCard, color: 'indigo' },
    ];

    return (
      <div className="bg-white rounded-lg border p-4 mt-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-lg flex items-center gap-2">
            {importStatus.running ? (
              <Loader2 className="w-5 h-5 text-blue-600 animate-spin" />
            ) : importStatus.error ? (
              <XCircle className="w-5 h-5 text-red-600" />
            ) : (
              <CheckCircle className="w-5 h-5 text-green-600" />
            )}
            {importStatus.running ? 'Importación en progreso...' : 
             importStatus.error ? 'Error en importación' : 
             'Importación completada'}
            {importStatus.triggered_by === 'scheduled' && (
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">Programada</span>
            )}
          </h3>
          
          {importStatus.running && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={cancelImport}
              className="text-red-600 border-red-600 hover:bg-red-50"
              data-testid="alegra-cancel-import-btn"
            >
              <StopCircle className="w-4 h-4 mr-1" /> Cancelar
            </Button>
          )}

          {!importStatus.running && (importStatus.error || importStatus.progress?.bills_status === 'cancelled' || importStatus.progress?.bills_status === 'saving') && (
            <Button
              size="sm"
              onClick={syncPendingNow}
              disabled={syncingPending}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="alegra-sync-pending-btn"
            >
              {syncingPending ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Sincronizando...</>
              ) : (
                <><RefreshCw className="w-4 h-4 mr-1" /> Sincronizar pendientes ahora</>
              )}
            </Button>
          )}
        </div>

        {lastIncrementalResult && (
          <div className="mb-4 p-3 bg-emerald-50 border border-emerald-200 rounded text-sm" data-testid="alegra-incremental-result">
            <div className="font-semibold text-emerald-800 mb-1">Sync incremental ejecutado</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs text-emerald-900">
              {lastIncrementalResult.invoices && (
                <div>📄 Facturas Venta: <span className="font-mono">{lastIncrementalResult.invoices.imported || 0}+{lastIncrementalResult.invoices.updated || 0} ↻</span></div>
              )}
              {lastIncrementalResult.bills && (
                <div>🧾 Facturas Compra: <span className="font-mono">{lastIncrementalResult.bills.imported || 0}+{lastIncrementalResult.bills.updated || 0} ↻</span></div>
              )}
              {lastIncrementalResult.credit_notes && (
                <div>📑 Notas Crédito: <span className="font-mono">{lastIncrementalResult.credit_notes.imported || 0}+{lastIncrementalResult.credit_notes.updated || 0} ↻</span></div>
              )}
              {lastIncrementalResult.payments && (
                <div>💳 Pagos: <span className="font-mono">{lastIncrementalResult.payments.imported || 0}+{lastIncrementalResult.payments.updated || 0} ↻</span></div>
              )}
            </div>
            {lastIncrementalResult.errors?.length > 0 && (
              <div className="text-xs text-red-700 mt-1">⚠️ {lastIncrementalResult.errors.join('; ')}</div>
            )}
          </div>
        )}

        {/* Iter 232.13 (Carlos) — Resync FULL de bills/invoices pendientes (saldos vivos) */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <div className="flex items-center justify-between mb-2">
            <div>
              <div className="font-semibold text-blue-900 text-sm flex items-center gap-2">
                <RefreshCw className="w-4 h-4" /> Resync de Saldos Pendientes (Alegra → CHIRINO)
              </div>
              <div className="text-xs text-blue-700 mt-0.5">
                Cierra cualquier gap del sync incremental (cap 120 docs/tipo). Recomendado cuando un proveedor reclame que su saldo no coincide.
              </div>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={resyncPendingBills} disabled={resyncing} variant="outline" className="border-blue-300" data-testid="resync-bills-btn">
              {resyncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Receipt className="w-4 h-4 mr-1" />}
              Resync facturas COMPRA pendientes
            </Button>
            <Button size="sm" onClick={resyncPendingInvoices} disabled={resyncing} variant="outline" className="border-blue-300" data-testid="resync-invoices-btn">
              {resyncing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <FileText className="w-4 h-4 mr-1" />}
              Resync facturas VENTA pendientes
            </Button>
            <Button size="sm" onClick={resyncBillByNumber} disabled={resyncing} variant="outline" className="border-blue-300" data-testid="resync-bill-by-number-btn">
              <Search className="w-4 h-4 mr-1" /> Rescatar bill por N°
            </Button>
          </div>
          {resyncJob && resyncJob.status === 'running' && (
            <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded p-2 text-amber-800">
              ⏳ Procesando... Página {resyncJob.iteraciones} · {resyncJob.processed} bills escaneadas · {resyncJob.nuevos} nuevas · {resyncJob.actualizados_saldo} con saldo cambiado
            </div>
          )}
          {resyncResult && (
            <div className="mt-2 text-xs space-y-1 bg-white border border-blue-200 rounded p-2">
              <div className="font-semibold text-blue-900">
                Resync {resyncResult.tipo} completado: {resyncResult.processed} procesadas · {resyncResult.nuevos} nuevas · {resyncResult.actualizados_saldo} con saldo actualizado · {resyncResult.iteraciones} páginas escaneadas
              </div>
              {resyncResult.muestra_nuevos?.length > 0 && (
                <details className="text-blue-800">
                  <summary className="cursor-pointer">Nuevas detectadas ({resyncResult.muestra_nuevos.length}):</summary>
                  <ul className="ml-4 mt-1 list-disc">
                    {resyncResult.muestra_nuevos.map((n, i) => (
                      <li key={i}>#{n.numero} {n.proveedor || n.cliente} — ${(n.saldo || 0).toLocaleString('es-CL')}</li>
                    ))}
                  </ul>
                </details>
              )}
              {resyncResult.muestra_actualizados_saldo?.length > 0 && (
                <details className="text-blue-800">
                  <summary className="cursor-pointer">Saldos cambiados ({resyncResult.muestra_actualizados_saldo.length}):</summary>
                  <ul className="ml-4 mt-1 list-disc">
                    {resyncResult.muestra_actualizados_saldo.map((n, i) => (
                      <li key={i}>#{n.numero} {n.proveedor || n.cliente}: ${(n.saldo_anterior || 0).toLocaleString('es-CL')} → ${(n.saldo_nuevo || 0).toLocaleString('es-CL')}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
          )}
        </div>

        {importStatus.error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded flex items-center gap-2 text-red-700">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm">{importStatus.error}</span>
          </div>
        )}

        {importStatus.started_at && (
          <p className="text-xs text-muted-foreground mb-4">
            Iniciado: {new Date(importStatus.started_at).toLocaleString('es-CL')}
            {importStatus.completed_at && ` | Finalizado: ${new Date(importStatus.completed_at).toLocaleString('es-CL')}`}
          </p>
        )}

        <div className="space-y-4">
          {steps.map(step => {
            const progress = getStepProgress(step.key, importStatus);
            const Icon = step.icon;
            // Activo SOLO si el job sigue corriendo. Si quedó interrumpido,
            // el spinner desaparece para no confundir al usuario (Carlos
            // 08/05/2026: spinner fantasma seguía girando tras cancelar).
            const isActive = importStatus.running && importStatus.current_step === step.key;
            const isInterrupted = !!progress.interrupted;
            
            return (
              <div key={step.key} className={`${isActive ? 'bg-blue-50 p-3 rounded -mx-2' : ''}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <Icon className={`w-4 h-4 text-${step.color}-500`} />
                    <span className="text-sm font-medium">{step.label}</span>
                    {isActive && <Loader2 className="w-3 h-3 animate-spin text-blue-500" />}
                    {isInterrupted && <AlertCircle className="w-3 h-3 text-red-500" />}
                  </div>
                  <span className={`text-xs ${isInterrupted ? 'text-red-600 font-medium' : 'text-muted-foreground'}`}>{progress.text}</span>
                </div>
                <Progress value={progress.percent} className={`h-2 ${isInterrupted ? 'opacity-50' : ''}`} />
              </div>
            );
          })}
        </div>

        {importStatus.progress?.final_results && (
          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded">
            <h4 className="font-medium text-green-800 mb-2">Resumen Final</h4>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-sm">
              {Object.entries(importStatus.progress.final_results).map(([key, val]) => (
                <div key={key}>
                  <span className="text-muted-foreground capitalize">{key}:</span>
                  <span className="ml-1 font-medium">{val.imported || 0} nuevos</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Integración Alegra</h1>
          <p className="text-sm text-muted-foreground">
            Importar datos desde Alegra: Facturas, Proveedores, Clientes, Pagos
          </p>
        </div>
      </div>

      {/* Connection Card */}
      <div className="bg-white rounded-lg border p-4">
        <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-blue-600" />
          Configuración de Conexión
        </h2>

        {importing && (
          <div className="mb-4 bg-amber-50 border-l-4 border-amber-500 p-3 rounded text-sm" data-testid="alegra-sync-running-notice">
            <strong className="text-amber-800">⏳ Sincronización en curso</strong>
            <p className="text-amber-700 mt-0.5">
              Los campos Email y Token están bloqueados por seguridad mientras hay un sync corriendo.
              Se desbloquearán automáticamente cuando termine. Puedes monitorear el progreso al final de esta página.
            </p>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium mb-1">Email de Alegra</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@empresa.cl"
              data-testid="alegra-email"
              disabled={importing}
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Token de API</label>
            <div className="relative">
              <Input
                type={showToken ? "text" : "password"}
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Token de Alegra"
                data-testid="alegra-token"
                disabled={importing}
              />
              <button
                type="button"
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                onClick={() => setShowToken(!showToken)}
              >
                {showToken ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Obténgalo en Alegra - Configuración - Integraciones - API
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <Button onClick={testConnection} disabled={testing || importing} data-testid="test-alegra-btn">
            {testing ? (
              <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Probando...</>
            ) : (
              'Probar Conexión'
            )}
          </Button>
          
          {connectionStatus && (
            <div className={`flex items-center gap-2 px-3 py-2 rounded ${
              connectionStatus.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
            }`}>
              {connectionStatus.success ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              <span className="text-sm">
                {connectionStatus.message}
                {connectionStatus.company && ` - ${connectionStatus.company}`}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Automatic Sync Configuration */}
      <div className="bg-white rounded-lg border p-4">
        <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Settings className="w-5 h-5 text-purple-600" />
          Sincronización Automática
        </h2>
        
        <div className="space-y-4">
          {/* Enable/Disable */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded">
            <div className="flex items-center gap-3">
              <Clock className="w-5 h-5 text-gray-600" />
              <div>
                <p className="font-medium">Sincronización Diaria Automática</p>
                <p className="text-sm text-muted-foreground">
                  Sincronizar automáticamente todos los días
                </p>
              </div>
            </div>
            <Switch
              checked={syncConfig.enabled}
              onCheckedChange={(checked) => setSyncConfig({...syncConfig, enabled: checked})}
              disabled={savingConfig}
            />
          </div>
          
          {/* Schedule Time */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Hora de ejecución</label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="0"
                  max="23"
                  value={syncConfig.schedule_hour}
                  onChange={(e) => setSyncConfig({...syncConfig, schedule_hour: parseInt(e.target.value) || 0})}
                  className="w-20"
                  disabled={savingConfig}
                />
                <span className="self-center">:</span>
                <Input
                  type="number"
                  min="0"
                  max="59"
                  value={syncConfig.schedule_minute}
                  onChange={(e) => setSyncConfig({...syncConfig, schedule_minute: parseInt(e.target.value) || 0})}
                  className="w-20"
                  disabled={savingConfig}
                />
              </div>
              <p className="text-xs text-muted-foreground mt-1">Hora local del servidor</p>
            </div>
            
            <div className="md:col-span-2">
              <label className="block text-sm font-medium mb-1">Email para notificaciones</label>
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={notificationEmail}
                  onChange={(e) => setNotificationEmail(e.target.value)}
                  placeholder="notificaciones@empresa.cl"
                  disabled={savingConfig}
                />
                <Button 
                  variant="outline" 
                  onClick={sendTestEmail}
                  disabled={testingEmail || !notificationEmail}
                  title="Enviar email de prueba"
                >
                  {testingEmail ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">Recibirá un reporte después de cada sincronización</p>
            </div>
          </div>
          
          {/* What to sync */}
          <div>
            <label className="block text-sm font-medium mb-2">Datos a sincronizar</label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={syncConfig.import_contacts}
                  onChange={(e) => setSyncConfig({...syncConfig, import_contacts: e.target.checked})}
                  className="w-4 h-4"
                  disabled={savingConfig}
                />
                <Users className="w-4 h-4 text-blue-500" />
                <span className="text-sm">Contactos</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={syncConfig.import_invoices}
                  onChange={(e) => setSyncConfig({...syncConfig, import_invoices: e.target.checked})}
                  className="w-4 h-4"
                  disabled={savingConfig}
                />
                <FileText className="w-4 h-4 text-green-500" />
                <span className="text-sm">Facturas Venta</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={syncConfig.import_bills}
                  onChange={(e) => setSyncConfig({...syncConfig, import_bills: e.target.checked})}
                  className="w-4 h-4"
                  disabled={savingConfig}
                />
                <Receipt className="w-4 h-4 text-orange-500" />
                <span className="text-sm">Facturas Compra</span>
              </label>
              
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={syncConfig.import_payments}
                  onChange={(e) => setSyncConfig({...syncConfig, import_payments: e.target.checked})}
                  className="w-4 h-4"
                  disabled={savingConfig}
                />
                <CreditCard className="w-4 h-4 text-purple-500" />
                <span className="text-sm">Pagos</span>
              </label>
            </div>
          </div>

          {/* Iter 235.m (Carlos): Capa 4 — Auto-asociar DTE entrante a OC con match alto */}
          <div className="rounded-lg border-2 border-emerald-200 bg-emerald-50/50 p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-lg">🎯</span>
                  <span className="font-semibold text-sm">Auto-asociar DTE entrante a OC</span>
                </div>
                <p className="text-xs text-muted-foreground">
                  Cuando llega una factura nueva y existe una OC del mismo proveedor con match
                  exacto (monto + fecha), el sistema la asocia automáticamente sin requerir tu intervención.
                </p>
              </div>
              <label className="flex items-center gap-2 cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={!!syncConfig.auto_asociar_dte_enabled}
                  onChange={(e) => setSyncConfig({ ...syncConfig, auto_asociar_dte_enabled: e.target.checked })}
                  className="w-4 h-4"
                  disabled={savingConfig}
                  data-testid="auto-asociar-toggle"
                />
                <span className="text-sm font-medium">Activado</span>
              </label>
            </div>
            {syncConfig.auto_asociar_dte_enabled && (
              <div className="mt-3 flex items-center gap-2">
                <label className="text-xs text-muted-foreground">Umbral mínimo (score 0-100):</label>
                <Input
                  type="number"
                  min={70} max={100}
                  value={syncConfig.auto_asociar_dte_threshold || 90}
                  onChange={(e) => setSyncConfig({ ...syncConfig, auto_asociar_dte_threshold: parseInt(e.target.value) || 90 })}
                  className="w-20 h-8 text-sm"
                  disabled={savingConfig}
                  data-testid="auto-asociar-threshold"
                />
                <span className="text-xs text-muted-foreground">
                  (recomendado: <b>90</b> — monto exacto + misma fecha. Subí a 100 sólo si querés match perfecto OT incluido.)
                </span>
              </div>
            )}
          </div>

          {/* Status and Actions */}
          <div className="flex items-center justify-between pt-4 border-t">
            <div className="text-sm">
              {syncConfig.enabled && schedulerStatus?.next_sync && (
                <p className="flex items-center gap-2 text-green-700">
                  <Calendar className="w-4 h-4" />
                  Próxima sincronización: {new Date(schedulerStatus.next_sync).toLocaleString('es-CL')}
                </p>
              )}
              {syncConfig.last_sync && (
                <p className="text-muted-foreground mt-1">
                  Última sincronización: {new Date(syncConfig.last_sync).toLocaleString('es-CL')}
                  {syncConfig.last_sync_status === 'error' && (
                    <span className="text-red-600 ml-2">(Error)</span>
                  )}
                </p>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={triggerSyncNow}
                disabled={importing || !connectionStatus?.success}
              >
                <Play className="w-4 h-4 mr-2" />
                Sincronizar Ahora
              </Button>
              
              <Button 
                onClick={saveSyncConfig}
                disabled={savingConfig}
              >
                {savingConfig ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Guardando...</>
                ) : (
                  'Guardar Configuración'
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Manual Import Options */}
      <div className="bg-white rounded-lg border p-4">
        <h2 className="font-semibold text-lg mb-4 flex items-center gap-2">
          <Download className="w-5 h-5 text-green-600" />
          Importación Manual
        </h2>
        
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={importOptions.contacts}
              onChange={(e) => setImportOptions({...importOptions, contacts: e.target.checked})}
              className="w-4 h-4"
              disabled={importing}
            />
            <Users className="w-4 h-4 text-blue-500" />
            <span className="text-sm">Contactos</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={importOptions.invoices}
              onChange={(e) => setImportOptions({...importOptions, invoices: e.target.checked})}
              className="w-4 h-4"
              disabled={importing}
            />
            <FileText className="w-4 h-4 text-green-500" />
            <span className="text-sm">Facturas Venta</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={importOptions.bills}
              onChange={(e) => setImportOptions({...importOptions, bills: e.target.checked})}
              className="w-4 h-4"
              disabled={importing}
            />
            <Receipt className="w-4 h-4 text-orange-500" />
            <span className="text-sm">Facturas Compra</span>
          </label>
          
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={importOptions.payments}
              onChange={(e) => setImportOptions({...importOptions, payments: e.target.checked})}
              className="w-4 h-4"
              disabled={importing}
            />
            <CreditCard className="w-4 h-4 text-purple-500" />
            <span className="text-sm">Pagos</span>
          </label>
        </div>

        <Button 
          onClick={importData} 
          disabled={importing || !connectionStatus?.success}
          className="bg-green-600 hover:bg-green-700"
          data-testid="import-alegra-btn"
        >
          {importing ? (
            <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importando en segundo plano...</>
          ) : (
            <><Download className="w-4 h-4 mr-2" /> Iniciar Importación</>
          )}
        </Button>

        {!connectionStatus?.success && (
          <p className="text-xs text-muted-foreground mt-2">
            Primero debe probar la conexión exitosamente
          </p>
        )}

        {/* Progress Section */}
        {(importStatus && (importStatus.running || importStatus.completed_at)) && renderProgressSection()}
      </div>

      {/* Iter 233 (Carlos) — Refrescar UN contacto Alegra sin esperar al cron */}
      <RefreshContactoCard />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-2 text-blue-600 mb-2">
            <Users className="w-5 h-5" />
            <span className="font-medium">Contactos</span>
          </div>
          <p className="text-2xl font-bold">
            {loadingStats ? '...' : fmt(stats?.contacts || 0)}
          </p>
          <p className="text-xs text-muted-foreground">Clientes y Proveedores</p>
        </div>

        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-2 text-green-600 mb-2">
            <FileText className="w-5 h-5" />
            <span className="font-medium">Facturas Venta</span>
          </div>
          <p className="text-2xl font-bold">
            {loadingStats ? '...' : fmt(stats?.invoices?.count || 0)}
          </p>
          <p className="text-xs text-muted-foreground">
            Total: ${fmt(stats?.invoices?.total)} | Pendiente: ${fmt(stats?.invoices?.saldo_pendiente)}
          </p>
        </div>

        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-2 text-orange-600 mb-2">
            <Receipt className="w-5 h-5" />
            <span className="font-medium">Facturas Compra</span>
          </div>
          <p className="text-2xl font-bold">
            {loadingStats ? '...' : fmt(stats?.bills?.count || 0)}
          </p>
          <p className="text-xs text-muted-foreground">
            Total: ${fmt(stats?.bills?.total)} | Pendiente: ${fmt(stats?.bills?.saldo_pendiente)}
          </p>
        </div>

        <div className="bg-white rounded-lg border p-4">
          <div className="flex items-center gap-2 text-purple-600 mb-2">
            <CreditCard className="w-5 h-5" />
            <span className="font-medium">Pagos</span>
          </div>
          <p className="text-2xl font-bold">
            {loadingStats ? '...' : fmt((stats?.payments?.recibidos || 0) + (stats?.payments?.realizados || 0))}
          </p>
          <p className="text-xs text-muted-foreground">
            Recibidos: {fmt(stats?.payments?.recibidos)} | Realizados: {fmt(stats?.payments?.realizados)}
          </p>
        </div>
      </div>

      {/* Refresh Stats Button */}
      <div className="flex justify-end gap-2">
        <AlegraSyncButton onSuccess={loadStats} />
        <Button variant="outline" onClick={loadStats} disabled={loadingStats}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loadingStats ? 'animate-spin' : ''}`} />
          Actualizar Estadísticas
        </Button>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h3 className="font-medium text-blue-800 mb-2">Información</h3>
        <ul className="text-sm text-blue-700 space-y-1">
          <li><strong>Sync incremental cada 5 min</strong> (Facturas Venta + Compra + Notas Crédito + Pagos): trae solo lo último.</li>
          <li><strong>Full sync semanal (domingo)</strong>: red de seguridad para capturar ediciones retroactivas en docs antiguos.</li>
          <li>Recibirá un email con el resumen después del full sync semanal.</li>
          <li>La importación se ejecuta en segundo plano - puede navegar a otras páginas.</li>
          <li>Los datos se importan a colecciones separadas prefijadas con "alegra_".</li>
          <li>Puede sincronizar múltiples veces; los registros existentes se actualizan.</li>
        </ul>
      </div>
    </div>
  );
};

export default AlegraIntegration;
